/**
 * /admin/intl-sellers — command center for international direct-charge
 * sellers. Roster (Stripe health + signed agreement evidence + volume),
 * per-seller actions (push/inbox notify, suspend/unsuspend, activity),
 * and an Issues worklist of recent intl bookings/orders with buyer
 * contact info + recorded acknowledgments.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeftIcon as ArrowLeft,
  GlobeHemisphereWestIcon as Globe,
  BellRingingIcon as BellRinging,
  ProhibitIcon as Prohibit,
  PlayCircleIcon as PlayCircle,
  ListMagnifyingGlassIcon as ListMagnifyingGlass,
  ArrowClockwiseIcon as ArrowClockwise,
} from "@phosphor-icons/react";

const STATUS_TONE = {
  confirmed: "text-emerald-700 bg-emerald-50 border-emerald-200",
  paid: "text-emerald-700 bg-emerald-50 border-emerald-200",
  completed: "text-emerald-700 bg-emerald-50 border-emerald-200",
  pending_payment: "text-[#8A5A00] bg-[#FFF8EC] border-[#E5A33B]/50",
  cancelled: "text-crimson bg-red-50 border-red-200",
};

const Chip = ({ ok, yes, no }) => (
  <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold ${ok ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-[#8A5A00] bg-[#FFF8EC] border-[#E5A33B]/50"}`}>
    {ok ? yes : no}
  </span>
);

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

function NotifyDialog({ seller, open, onClose }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTitle(""); setBody(""); } }, [open]);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/intl-sellers/${seller.user_id}/notify`, { title, body });
      toast.success(`Sent to ${seller.name} — inbox + push`);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="intl-notify-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-xl">Notify {seller?.name}</DialogTitle>
          <DialogDescription>Sends an inbox message, bell notification, and a push to their phone in one shot.</DialogDescription>
        </DialogHeader>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title — e.g. A buyer is waiting on order #123" data-testid="intl-notify-title" />
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Message…" data-testid="intl-notify-body" />
        <DialogFooter>
          <Button onClick={send} disabled={!title.trim() || !body.trim() || busy} className="rounded-full bg-ink hover:bg-crimson text-white w-full" data-testid="intl-notify-send">
            {busy ? "Sending…" : "Send inbox + push"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActivityDialog({ seller, open, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open || !seller) return;
    setData(null);
    api.get(`/admin/intl-sellers/${seller.user_id}/activity`).then((r) => setData(r.data)).catch(() => setData({ bookings: [], orders: [] }));
  }, [open, seller]);
  const Row = ({ label, when, status, amount }) => (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-[#F1F1F1] text-[12px]">
      <span className="truncate">{label}</span>
      <span className="text-[#9A9A9A] flex-shrink-0">{fmtDate(when)}</span>
      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold flex-shrink-0 ${STATUS_TONE[status] || "text-[#6B6B6B] bg-[#F4F4F4] border-[#E0E0E0]"}`}>{status}</span>
      <span className="font-semibold flex-shrink-0">${Number(amount || 0).toFixed(2)}</span>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="intl-activity-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-xl">{seller?.name} — activity</DialogTitle>
        </DialogHeader>
        {!data ? <p className="text-sm text-[#9A9A9A]">Loading…</p> : (
          <div className="space-y-4 max-h-[50vh] overflow-y-auto">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B] mb-1">Bookings ({data.bookings.length})</p>
              {data.bookings.length === 0 && <p className="text-[12px] text-[#9A9A9A]">None yet.</p>}
              {data.bookings.map((b) => <Row key={b.id} label={b.service_name} when={b.created_at} status={b.status} amount={b.customer_total || b.amount} />)}
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B] mb-1">Store orders ({data.orders.length})</p>
              {data.orders.length === 0 && <p className="text-[12px] text-[#9A9A9A]">None yet.</p>}
              {data.orders.map((o) => <Row key={o.id} label={`${o.product_name} — ${o.customer_name}`} when={o.created_at} status={o.status} amount={(o.amount_cents || 0) / 100} />)}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminIntlSellers() {
  const [data, setData] = useState(null);
  const [issues, setIssues] = useState(null);
  const [tab, setTab] = useState("sellers");
  const [notifyFor, setNotifyFor] = useState(null);
  const [activityFor, setActivityFor] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/intl-sellers").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/intl-sellers/issues").then((r) => setIssues(r.data.issues)).catch(() => setIssues([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleSuspend = async (s) => {
    const suspending = !s.selling_suspended;
    if (suspending && !window.confirm(`Suspend selling for ${s.name}? Their store + bookings stop accepting new payments immediately.`)) return;
    setBusyId(s.user_id);
    try {
      if (suspending) {
        const reason = window.prompt("Reason (recorded, shown only to you):", "") ?? "";
        await api.post(`/admin/intl-sellers/${s.user_id}/suspend`, { reason });
        toast.success(`${s.name} suspended — no new sales possible`);
      } else {
        await api.post(`/admin/intl-sellers/${s.user_id}/unsuspend`);
        toast.success(`${s.name} can sell again`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-intl-sellers-page">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-[12px] text-[#6B6B6B] hover:text-ink mb-4">
          <ArrowLeft size={14} /> Admin console
        </Link>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight flex items-center gap-2.5">
              <Globe size={30} weight="duotone" className="text-gold" /> International Sellers
            </h1>
            <p className="text-[13px] text-[#6B6B6B] mt-1 max-w-2xl">
              Direct-charge sellers (merchant of record on their own Stripe). Agreement
              records are your legal evidence; suspend stops new sales instantly.
            </p>
          </div>
          <Button variant="outline" onClick={load} className="rounded-full h-9" data-testid="intl-sellers-refresh">
            <ArrowClockwise size={15} className="mr-1.5" /> Refresh
          </Button>
        </div>

        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[["Total sellers", data.summary.total], ["Live", data.summary.live], ["Pending Stripe", data.summary.pending_stripe], ["Suspended", data.summary.suspended]].map(([k, v]) => (
              <div key={k} className="rounded-xl border border-[#EAEAEA] bg-white p-4" data-testid={`intl-stat-${k.toLowerCase().replace(/ /g, "-")}`}>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold mb-1">{k}</div>
                <div className="text-2xl font-serif">{v}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mb-5">
          {[["sellers", "Seller roster"], ["issues", "Issues & orders"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`rounded-full px-4 h-9 text-[13px] font-semibold border transition-colors ${tab === k ? "bg-ink text-white border-ink" : "bg-white text-[#6B6B6B] border-[#E0E0E0] hover:border-ink"}`}
              data-testid={`intl-tab-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "sellers" && (
          <div className="space-y-3" data-testid="intl-seller-list">
            {!data && <p className="text-sm text-[#9A9A9A]">Loading…</p>}
            {data && data.sellers.length === 0 && (
              <div className="rounded-xl border border-dashed border-[#D8D8D8] bg-white p-8 text-center text-[13px] text-[#6B6B6B]">
                No international sellers yet. They'll appear here the moment a
                non-US seller accepts the agreement and starts Stripe setup.
              </div>
            )}
            {data?.sellers.map((s) => (
              <div key={s.user_id} className={`rounded-xl border bg-white p-4 sm:p-5 ${s.selling_suspended ? "border-crimson/40" : "border-[#EAEAEA]"}`} data-testid="intl-seller-row">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-bold text-[14px]">
                      {s.name} <span className="ml-1 text-[11px] font-mono font-normal text-[#6B6B6B]">{s.country}</span>
                      {s.selling_suspended && <span className="ml-2 rounded-full border border-crimson/40 bg-red-50 text-crimson px-2 py-0.5 text-[10px] font-bold">SUSPENDED</span>}
                    </p>
                    <p className="text-[12px] text-[#6B6B6B]">{s.email}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <Chip ok={s.charges_enabled} yes="Charges ✓" no="Charges pending" />
                      <Chip ok={s.payouts_enabled} yes="Payouts ✓" no="Payouts pending" />
                      <span className="text-[11px] text-[#6B6B6B]">
                        {s.bookings_count} bookings · {s.orders_count} orders · onboarded {fmtDate(s.onboarded_at)}
                      </span>
                    </div>
                    <p className="text-[11px] text-[#9A9A9A] mt-1.5" data-testid="intl-agreement-evidence">
                      Agreement: {s.agreement ? (
                        <>signed <strong>{s.agreement.version}</strong> on {fmtDate(s.agreement.accepted_at)} · IP {s.agreement.ip || "—"}</>
                      ) : "⚠ no record"}
                      {s.selling_suspended && s.suspended_reason && <> · Suspension reason: {s.suspended_reason}</>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => setNotifyFor(s)} data-testid="intl-notify-btn">
                      <BellRinging size={14} className="mr-1" /> Notify
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => setActivityFor(s)} data-testid="intl-activity-btn">
                      <ListMagnifyingGlass size={14} className="mr-1" /> Activity
                    </Button>
                    <Button
                      size="sm"
                      className={`rounded-full h-8 text-white ${s.selling_suspended ? "bg-emerald-700 hover:bg-emerald-800" : "bg-crimson hover:bg-ink"}`}
                      disabled={busyId === s.user_id}
                      onClick={() => toggleSuspend(s)}
                      data-testid="intl-suspend-btn"
                    >
                      {s.selling_suspended ? <><PlayCircle size={14} className="mr-1" /> Unsuspend</> : <><Prohibit size={14} className="mr-1" /> Suspend</>}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "issues" && (
          <div className="rounded-xl border border-[#EAEAEA] bg-white overflow-hidden" data-testid="intl-issues-list">
            {!issues && <p className="p-6 text-sm text-[#9A9A9A]">Loading…</p>}
            {issues && issues.length === 0 && (
              <p className="p-8 text-center text-[13px] text-[#6B6B6B]">
                No international orders or bookings yet — this fills in as sales happen.
              </p>
            )}
            {issues?.map((r) => (
              <div key={`${r.type}-${r.id}`} className="px-4 py-3 border-b border-[#F1F1F1] last:border-0 flex items-center justify-between gap-3 flex-wrap" data-testid="intl-issue-row">
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold truncate">
                    {r.type === "booking" ? "Booking" : "Store order"} — {r.service_title}
                    <span className="ml-2 font-normal text-[#6B6B6B]">${Number(r.amount_usd || 0).toFixed(2)}</span>
                  </p>
                  <p className="text-[11.5px] text-[#6B6B6B]">
                    Buyer: {r.buyer_name} ({r.buyer_email}) · Seller: {r.seller_name} · {fmtDate(r.created_at)}
                    {r.buyer_ack_at ? " · buyer ack ✓ recorded" : " · ⚠ no buyer ack"}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${STATUS_TONE[r.status] || "text-[#6B6B6B] bg-[#F4F4F4] border-[#E0E0E0]"}`}>{r.status}</span>
                  <Button
                    size="sm" variant="outline" className="rounded-full h-8"
                    onClick={() => setNotifyFor(data?.sellers.find((s) => s.user_id === r.seller_user_id) || { user_id: r.seller_user_id, name: r.seller_name })}
                    data-testid="intl-issue-notify-seller"
                  >
                    <BellRinging size={14} className="mr-1" /> Ping seller
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <NotifyDialog seller={notifyFor} open={!!notifyFor} onClose={() => setNotifyFor(null)} />
        <ActivityDialog seller={activityFor} open={!!activityFor} onClose={() => setActivityFor(null)} />
      </div>
    </AppShell>
  );
}
